/**
 * accordion.js
 *
 * @version 1.0.0
 */

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */

const ATTR_ACCORDION = 'data-accordion';
const ATTR_CONTENT = 'data-accordion-content';
const ATTR_EXPANDED = 'data-expanded';
const ATTR_CLOSING = 'data-closing';

/* ----------------------------------------------------------------------- */
/*  Event handlers                                                         */
/* ----------------------------------------------------------------------- */

/**
 * Intercept clicks on <summary> so we can animate closing.
 *
 * @param {MouseEvent} event
 */
function handleClick(event) {
	// Only care about summary clicks inside an accordion
	const summary = event.target.closest(`[${ATTR_ACCORDION}] > summary`);
	if (!summary) return;

	const accordion = summary.parentElement;
	const panel = accordion.querySelector(`[${ATTR_CONTENT}]`);


	/* --------------------------------------------------- *
	 * OPEN  – let the native <details> behaviour run
	 * CLOSE – prevent native toggle & run our animation
	 * --------------------------------------------------- */
	if (accordion.open) {
		// User is closing an open accordion
		event.preventDefault(); // keep it open for now
		collapse(panel, accordion);
	} else {
		expand(panel, accordion);
	}
}

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */

/**
 * Smoothly expands the accordion panel from 0 to its scrollHeight.
 *
 * @param {HTMLElement} panel – The element that physically animates.
 * @param {HTMLDetailsElement} accordion – The <details> wrapper (used for state only).
 * @returns {void}
 */
function expand(panel, accordion) {
	// Explicitly set the starting height so the browser has two concrete
	// values to interpolate between (0 ➜ scrollHeight).
	panel.style.height = '0px';

	// Use rAF so the style change above is committed before we set the end value.
	requestAnimationFrame(() => {
		panel.setAttribute(ATTR_EXPANDED, ''); // mark as expanded
		panel.style.height = `${panel.scrollHeight}px`; // trigger transition
	});

	// Once the transition finishes, clear the inline height so the content
	// can grow/shrink naturally without additional JS.
	panel.addEventListener('transitionend', () => {
		panel.style.height = '';
	}, { once: true });
}

/**
 * Animates the panel closed, then delegates to the native <details> logic
 * to actually collapse the element once the animation finishes.
 *
 *
 * @param {HTMLElement} panel – The element that physically animates.
 * @param {HTMLDetailsElement} accordion – The <details> wrapper whose `.open` property we ultimately toggle.
 * @returns {void}
 */
function collapse(panel, accordion) {
	// Guard: ignore if a close animation is already running
	if (accordion.hasAttribute(ATTR_CLOSING)) return;
	accordion.setAttribute(ATTR_CLOSING, '');

	// Starting height is the element's current rendered height
	const startHeight = panel.scrollHeight;
	panel.style.height = `${startHeight}px`;

	// In the next frame, set the height to zero to kick off the transition
	requestAnimationFrame(() => {
		panel.style.height = '0px';
	});

	// Clean‑up after the transition completes
	panel.addEventListener('transitionend', () => {
		accordion.open = false; // collapse native <details>
		accordion.removeAttribute(ATTR_CLOSING);
		panel.removeAttribute(ATTR_EXPANDED);
		panel.style.height = ''; // reset for next open
	}, { once: true });
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Attach accordion behaviour to `document`.
 *
 * @returns {() => void}
 */
export function initAccordions() {
	document.addEventListener('click', handleClick, false);


	/* -------------------------------------------------------------- *
	 * Keep footer accordions open ≥ 1024 px and restore interactivity
	 * below that breakpoint.                                          *
	 * -------------------------------------------------------------- */

	const mql = window.matchMedia('(min-width: 1024px)');

	/**
	 * Sync the <details> `open` state with the current viewport.
	 *
	 * @param {MediaQueryListEvent | void} [evt]
	 * @returns {void}
	 */
	function setFooterAccordionState(evt) {
		const desktop = evt ? evt.matches : mql.matches;

		document.querySelectorAll('.accordion--footer').forEach((accordion) => {
			const panel = accordion.querySelector(`[${ATTR_CONTENT}]`);

			if (desktop) {
				/* force‑open on desktop */
				accordion.open = true;
				panel.setAttribute(ATTR_EXPANDED, '');
				panel.style.height = ''; // allow natural height
			} else {
				/* collapse (unless user has already opened it) */
				if (!accordion.hasAttribute(ATTR_CLOSING)) {
					accordion.open = false;
					panel.removeAttribute(ATTR_EXPANDED);
					panel.style.height = '0px';
				}
			}
		});
	}

	/* run once on load and again whenever the breakpoint flips */
	setFooterAccordionState();
	mql.addEventListener('change', setFooterAccordionState);

}
